import React from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useAppColors } from '../../hooks/useAppColors';
import { useAuthStore } from '../../stores/authStore';
import { usePickupPersons } from '../../hooks/usePickupPersons';
import { useFamilyMemberships } from '../../hooks/usePayments';
import { useTranslation } from 'react-i18next';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

interface PickupSetupPromptProps {
  visible: boolean;
  onDismiss: () => void;
  onSetupNow: () => void;
}

export default function PickupSetupPrompt({ visible, onDismiss, onSetupNow }: PickupSetupPromptProps) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const navigation = useNavigation();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: colors.card }]}>
          <View style={[styles.iconContainer, { backgroundColor: colors.accent + '20' }]}>
            <Icon name="car-child-seat" size={48} color={colors.accent} />
          </View>
          
          <Text style={[styles.title, { color: colors.text }]}>
            {t('pickup.promptTitle')}
          </Text>
          
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            {t('pickup.promptBody')}
          </Text>

          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton, { borderColor: colors.border }]}
              onPress={onDismiss}
            >
              <Text style={[styles.cancelButtonText, { color: colors.textSecondary }]}>
                {t('pickup.promptLater')}
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.button, styles.setupButton, { backgroundColor: colors.accent }]}
              onPress={onSetupNow}
            >
              <Text style={[styles.setupButtonText, { color: colors.accentText }]}>
                {t('pickup.promptCta')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

export function usePickupSetupPrompt() {
  const { t } = useTranslation();
  const navigation = useNavigation<any>();

  const { pickupSetupPrompted, setPickupSetupPrompted, hydrated } = useAuthStore();
  const { data: pickupPersons, isLoading: isLoadingPickup } = usePickupPersons();
  const { data: familyMemberships, isLoading: isLoadingFamilyMemberships } = useFamilyMemberships();

  const hasDependents = Boolean(familyMemberships && familyMemberships.length > 1);

  const shouldShow =
    hydrated &&
    hasDependents &&
    (pickupPersons?.length ?? 0) === 0 &&
    !pickupSetupPrompted &&
    !isLoadingFamilyMemberships &&
    !isLoadingPickup;

  const handleSetupNow = () => {
    setPickupSetupPrompted(true);
    navigation.navigate('PickupPersons' as any);
  };

  const handleMaybeLater = () => {
    setPickupSetupPrompted(true);
  };

  return {
    shouldShow,
    handleSetupNow,
    handleMaybeLater,
  };
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    maxWidth: 320,
    width: '100%',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    borderWidth: 1,
    backgroundColor: 'transparent',
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  setupButton: {
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  setupButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
});